import cx from 'classnames';
import { getColumns, getTableDataSet, prepareTableData, validateData } from 'helpers/TableHelper';
import { useGetTableTemplate } from 'hooks/useGetTableTemplates';
import { RenderTable } from 'pages/documents_v2/views/edit_area/blocks/block_types/table/RenderTable';
import { AreaContext } from 'pages/documents_v2/views/edit_area/context/AreaContext';
import { EditAreaContext } from 'pages/documents_v2/views/edit_area/context/EditAreaContext';
import { EditTableModal } from 'pages/documents_v2/views/edit_area/modals/EditTableModal';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import HelperFunctions from '../../../../../global/HelperFunctions';
import { useGetBaseVariant } from '../../../../hooks/useGetBaseVariant';
import { useGetDocument } from '../../../../hooks/useGetDocument';

export function TableBlock({ block, baseBlock, index, restrictions, readOnly, dirty = false }) {
    const { activeVariantId } = useContext(AreaContext);
    const document = useGetDocument();
    const baseVariant = useGetBaseVariant();
    const [showEditorModal, setShowEditorModal] = useState(false);
    const { t } = useTranslation('documents');
    const { hideChanges } = useContext(EditAreaContext);

    const blockContent = useMemo(() => {
        if (dirty) {
            return block.latestContent || block.baseContent || '';
        }

        if (baseBlock) {
            return hideChanges ? baseBlock.latestContentRendered : baseBlock.renderedDiffContent;
        }

        if (hideChanges === false && Object.hasOwn(block, 'renderedDiffContent')) {
            return block.renderedDiffContent;
        }

        return block.latestContent || block.baseContent || '';
    }, [block, hideChanges, baseBlock, dirty]);

    const table = HelperFunctions.tryParseJSON(blockContent, {
        data: {},
        templateId: false,
        title: '',
    });

    const template = useGetTableTemplate(parseInt(table.templateId));

    if (template === undefined) {
        return null;
    }

    const properties = {
        ...template?.properties,
        ...table?.properties,
    };

    const differentCellValuePerVariant = properties?.differentCellValuePerVariant ?? false;
    const baseVariantOverride = differentCellValuePerVariant === false && activeVariantId !== baseVariant.id;

    const tableDataSet = (() => {
        const dataSet = getTableDataSet(table, baseVariantOverride ? baseVariant.id : activeVariantId);

        // Fallback dataset (for new tables)
        if (dataSet === undefined) {
            return {
                variantId: activeVariantId,
            };
        }

        return dataSet;
    })();

    const packageGroupId = tableDataSet?.packageGroupId ?? undefined;
    const columns = getColumns(template, packageGroupId, activeVariantId, document);
    const rows = template?.rows;
    const highlightFirstColumn = block.properties?.highlightFirstColumn ?? false;

    const dataSet = prepareTableData(
        tableDataSet,
        rows,
        columns,
        {
            table: {
                interactiveChanges: false,
                markChanges: false,
            },
        },
        block,
    );

    const dataIsValid = validateData(table, dataSet, template?.rows ?? [], columns);
    const isReadOnly = readOnly === true || restrictions?.canChangeContent === false || baseVariantOverride;

    return (
        <>
            <div
                className={cx('table-wrapper p-4', {
                    editable: !isReadOnly,
                })}
                onClick={() => {
                    if (!isReadOnly) {
                        setShowEditorModal(true);
                    }
                }}
            >
                {dataIsValid.colsAreValid && dataIsValid.rowsAreValid && dataIsValid.dataSetHasData ? (
                    <>
                        {baseVariantOverride && (
                            <div className="font-weight-bold mb-3">
                                *&nbsp;
                                {t('documents:document.navbar.main.editor.left.blocks.types.tableBlock.tableData')}
                            </div>
                        )}

                        <RenderTable
                            baseBlock={baseBlock}
                            columns={columns}
                            enableTableEditor={false}
                            rows={rows}
                            tableData={dataSet.rows}
                            template={template}
                            highlightFirstColumn={highlightFirstColumn}
                            dirty={dirty}
                            readOnly={isReadOnly}
                            hideChanges={hideChanges}
                        />
                    </>
                ) : (
                    <div className="small text-secondary text-center py-2">
                        {packageGroupId === undefined
                            ? t('document.navbar.main.editor.left.blocks.types.tableBlock.noPackageGroupSelected')
                            : t('document.navbar.main.editor.left.blocks.types.tableBlock.noColumnsAreSet')}
                    </div>
                )}
            </div>

            {showEditorModal && (
                <EditTableModal
                    hide={() => setShowEditorModal(false)}
                    block={block}
                    document={document}
                    index={index}
                    packageGroups={document.packageGroups}
                />
            )}
        </>
    );
}
